import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import {
  listItems,
  toggleEquip,
  getEquipmentLayout,
  autoEquipBest,
} from "@/lib/game/items.functions";
import { maxEquipLevel } from "@/lib/game/types";
import {
  deriveSlot,
  evaluateUpgrade,
  isEquipKind,
  itemScore,
  MAX_ACCESSORY_SLOTS,
} from "@/lib/game/item-score";
import {
  useConsumable,
  sellItem,
  sellPrice,
  sellAllBelow,
  sellEquipmentBulk,
} from "@/lib/game/shop.functions";
import { getMyMonster } from "@/lib/game/monster.functions";
// hatchEgg/regenerate sind in die Brutkammer gewandert
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { InfoTip } from "@/components/game/InfoTip";
import { Panel } from "@/components/game/Panel";
import {
  FilterPills,
  GoldAmount,
  ItemIcon,
  MicroLabel,
  PageHeader,
  ScreenContainer,
} from "@/components/game/ScreenKit";
import { Pill, RarityTag, type Rarity } from "@/components/game/Badges";

import { useTutorial } from "@/components/game/TutorialProvider";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  ArrowUpDown,
  Backpack,
  Coins,
  FlaskConical,
  Footprints,
  Gem,
  Hand,
  HardHat,
  Lock,
  Package,
  PackageMinus,
  PersonStanding,
  Search,
  Shirt,
  Sparkles,
  Sword,
  Wand2,
  X,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";

const RARITY_RANK: Record<string, number> = {
  legendary: 4,
  epic: 3,
  rare: 2,
  common: 1,
};

const RARITIES: readonly string[] = ["common", "rare", "epic", "legendary"];

/**
 * Die Raritäts-Spalte ist in der DB ein freier String. Für die Design-Tokens
 * (`--rarity-*`) und `RarityTag` brauchen wir aber eine der vier bekannten
 * Stufen — alles andere fällt auf `common` zurück, statt eine leere CSS-Variable
 * zu erzeugen.
 */
function asRarity(raw: string | null | undefined): Rarity {
  return RARITIES.includes(raw ?? "") ? (raw as Rarity) : "common";
}

/**
 * ⚠️ `RarityTag` ohne `label` fällt auf seine fest deutschen Labels zurück
 * (`RARITY_LABELS` in `components/game/Badges.tsx`, nicht exportiert, nirgends
 * übersetzt) — in der englischen Fassung stünde „GEWÖHNLICH" neben englischem
 * Text. Der Aufrufer bringt die Übersetzung deshalb mit, wie in
 * `_app.game.breeding.tsx`.
 * 📌 Gehört als i18n-Schlüssel nach `Badges.tsx` — gemeinsame Datei, steht als
 * offener Punkt im Ergebnis.
 */
const RARITY_LABELS: Record<Rarity, { de: string; en: string }> = {
  common: { de: "Gewöhnlich", en: "Common" },
  rare: { de: "Selten", en: "Rare" },
  epic: { de: "Episch", en: "Epic" },
  legendary: { de: "Legendär", en: "Legendary" },
};

function rarityLabel(rarity: Rarity): string {
  const b = RARITY_LABELS[rarity];
  return currentLang() === "en" ? b.en : b.de;
}

/** Outline in der Raritätsfarbe — Hierarchie-Ebene 3 der Designsprache. */
function rarityBorder(rarity: Rarity): React.CSSProperties {
  return { borderColor: `var(--rarity-${rarity})` };
}

/**
 * Maßstab für die Sortierung „Stärke" — DIESELBE Funktion, aus der auch die
 * Markierung „Besser als deins" auf derselben Karte kommt (`evaluateUpgrade`)
 * und nach der der Server anlegt bzw. verwertet (`autoEquipBest`,
 * Auto-Salvage).
 *
 * ⚠️ Hier stand vorher die rohe Spalte `items.power`. Die ist
 * `int NOT NULL DEFAULT 0` und `buyItem` schreibt sie nicht mit — jedes im
 * Laden gekaufte Ausrüstungsteil sortierte damit als 0 ans Ende der
 * Voreinstellung „Stärke ↓" und trug gleichzeitig die grüne Pille „Besser als
 * deins", weil die aus `itemScore` (mit Rückfall auf `powerOf(stats)`) kommt.
 * Dieselbe Karte behauptete also „schwächstes Teil" und „besser als deins".
 *
 * Nicht-Ausrüstung bleibt bewusst bei 0: `itemScore` würde für einen Trank
 * dessen Heilwert als Kampfwert ausgeben (Phönix-Phiole: `{hp: 9999}` →
 * 99.990) und ihn vor jede Waffe schieben. Verbrauchsgegenstände haben keine
 * Stärke — das war vorher so und bleibt so.
 */
function strengthForSort(it: { kind: string; power?: number | null; stats?: unknown }): number {
  return isEquipKind(it.kind) ? itemScore(it) : 0;
}

/**
 * Item-Icons sind lucide-Platzhalter — echte Item-Grafiken gibt es nicht
 * (Design-Handoff, Abschnitt „Offen"). Der Slot gewinnt vor der Art, damit ein
 * Helm auch als Helm erkennbar ist und nicht als generische Rüstung.
 */
const SLOT_ICON: Record<string, LucideIcon> = {
  weapon: Sword,
  helm: HardHat,
  chest: Shirt,
  pants: PersonStanding,
  boots: Footprints,
  gloves: Hand,
  accessory: Gem,
};

const KIND_ICON: Record<string, LucideIcon> = {
  weapon: Sword,
  armor: Shirt,
  accessory: Gem,
  consumable: FlaskConical,
};

type SortKey = "power" | "rarity" | "kind" | "value" | "name" | "level";
type KindFilter = "all" | "equipment" | "consumable" | "equipped";

const KIND_FILTER_VALUES: KindFilter[] = ["all", "equipped", "equipment", "consumable"];

// Bekannte Stat-Keys, die wir im Inventar anzeigen. Schützt uns davor,
// nicht-numerische Meta-Felder wie `base` (Forge-Format) oder neue
// Server-Felder versehentlich als React-Kind zu rendern (React #31).
//
// ⚠️ `spd` steht hier bewusst NICHT mehr (Stefan, 09.09.2026): Tempo wird im
// Zonenkampf in keiner Formel gelesen, wurde dem Spieler aber als Kaufargument
// und als Verbesserung angezeigt. Der Wert bleibt in der Datenbank stehen —
// er wird nur nicht mehr gezeigt, damit eine Rückkehr eine reine Anzeige-
// änderung bleibt. Siehe `docs/planung/entscheidungen/kampfsystem.md`.
//
// Diese eine Liste speist drei Anzeigen zugleich: die Stat-Pillen auf jeder
// Item-Karte, die Pillen auf den belegten Ausrüstungs-Kacheln und die
// Vergleichspfeile gegen das angelegte Teil.
const STAT_KEYS = ["atk", "def", "hp_max", "hp"] as const;

function readStats(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!(STAT_KEYS as readonly string[]).includes(k)) continue;
    if (typeof v !== "number" || !Number.isFinite(v)) continue;
    out[k] = v;
  }
  return out;
}

// Slot-Ableitung: bewusst KEINE eigene Kopie mehr, sondern dieselbe Funktion,
// die der Server für Auto-Equip und Auto-Salvage benutzt. Der Wrapper macht
// nur aus `null` einen Leerstring, weil die Aufrufer hier mit Strings rechnen.
function slotOfItem(it: { kind: string; slot?: string | null; name?: string | null }): string {
  return deriveSlot(it) ?? "";
}

function iconOfItem(it: { kind: string; slot?: string | null; name?: string | null }): LucideIcon {
  return SLOT_ICON[slotOfItem(it)] ?? KIND_ICON[it.kind] ?? Package;
}

/**
 * Kleinstes Kreatur-Level, mit dem ein Item dieses Levels getragen werden darf.
 *
 * ⚠️ NICHT `itemLevel − (cap − aktuelles Level)` rechnen — genau das stand
 * vorher im Tooltip und nannte dem Spieler eine zu hohe Zahl. Der Bonus aus
 * `maxEquipLevel` ist gestaffelt (<10 → +3, <20 → +5, <30 → +8, sonst +12),
 * er wächst also mit. Die Subtraktion nahm den Bonus des AKTUELLEN Levels und
 * unterschlug damit, dass die nächste Stufe schon mehr Spielraum gibt
 * (Monster Lv. 5, Item Lv. 20: angezeigt wurde „Lv. 17", nötig ist Lv. 15).
 *
 * Gesucht ist deshalb die kleinste Stufe, für die `maxEquipLevel` das Item
 * bereits zulässt — geprüft gegen dieselbe Funktion, nach der auch der Server
 * entscheidet, statt eine zweite Formel danebenzustellen. Die Schleife endet
 * spätestens bei `itemLevel` selbst, weil der Bonus nie negativ wird.
 */
function requiredLevelForItem(itemLevel: number): number {
  const target = Math.max(1, Math.floor(itemLevel));
  for (let lvl = 1; lvl < target; lvl++) {
    if (maxEquipLevel(lvl) >= target) return lvl;
  }
  return target;
}

/**
 * Ein Stat-Chip — die EINE Form, in der ein Ausrüstungs- oder Trank-Wert auf
 * diesem Screen erscheint. Sie steht hier einmal, weil sie an zwei Stellen
 * gebraucht wird: in der Item-Karte und in der belegten Ausrüstungs-Kachel.
 * In der Kachel fehlten die Werte zuletzt komplett — man konnte einem
 * angelegten Teil nicht mehr ansehen, was es überhaupt bringt.
 *
 * `surfaceClassName`, weil der Grund verschieden ist: Die Karte liegt auf
 * `card` (Chip in `ink`), die Kachel ist selbst `ink` (Chip in `secondary`).
 */
function StatChip({
  statKey,
  value,
  compact = false,
  struck = false,
  surfaceClassName,
  children,
}: {
  statKey: string;
  value: number;
  /** Engere Variante für die schmalen Ausrüstungs-Kacheln. */
  compact?: boolean;
  /** Wert wirkungslos (Trank auf vollem Wert) — durchgestrichen statt grün. */
  struck?: boolean;
  surfaceClassName?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation("game");
  return (
    <span
      className={cn(
        "inline-flex items-baseline gap-1 rounded-md",
        compact ? "px-1.5 py-0.5" : "px-2 py-1",
        surfaceClassName ?? "bg-ink",
      )}
    >
      <b
        className={cn(
          "font-display font-normal tabular-nums",
          compact ? "text-xs" : "text-sm",
          struck ? "text-muted-foreground line-through" : "text-hp-ally",
        )}
      >
        +{value}
      </b>
      <span className="font-display text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
        {t(`inventory.stats.${statKey}`, statKey)}
      </span>
      {children}
    </span>
  );
}

import { RouteError, RouteNotFound } from "@/components/game/RouteError";
import { tServerError } from "@/lib/i18n/server-error";
import { currentLang } from "@/lib/i18n/lang";

export const Route = createFileRoute("/_app/game/inventory")({
  component: Inventory,
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  head: () => ({
    meta: [
      { title: "Inventory – Xenomons" },
      {
        name: "description",
        content:
          "Manage your Xenomons gear, consumables and special items. Equip weapons, armor and accessories.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Xenomons Inventory – Manage Your Team" },
      {
        property: "og:description",
        content: "Equipment slots, consumables and special items for your Xenomons roster.",
      },
    ],
  }),
});

function Inventory() {
  useTutorial("inventory_intro");
  const { t } = useTranslation("game");
  const qc = useQueryClient();
  const fetchItems = useServerFn(listItems);
  const fetchLayout = useServerFn(getEquipmentLayout);
  const equip = useServerFn(toggleEquip);
  const consumeItemFn = useServerFn(useConsumable);
  const sellFn = useServerFn(sellItem);
  const sellBulk = useServerFn(sellAllBelow);
  const sellEquipBulkFn = useServerFn(sellEquipmentBulk);
  const autoEquipFn = useServerFn(autoEquipBest);
  const fetchMonster = useServerFn(getMyMonster);
  const { data, isLoading } = useQuery({
    queryKey: ["items"],
    queryFn: () => fetchItems(),
  });
  const { data: layout } = useQuery({
    queryKey: ["equipment-layout"],
    queryFn: () => fetchLayout(),
    staleTime: 30_000,
  });
  const { data: monsterData } = useQuery({
    queryKey: ["my-monster"],
    queryFn: () => fetchMonster(),
    staleTime: 10_000,
  });
  const activeMonster = monsterData?.monster ?? null;
  useTutorial("bulk_sell_coach", (data?.items?.length ?? 0) >= 10);

  const [sortKey, setSortKey] = useState<SortKey>("power");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const [search, setSearch] = useState("");

  const sortedItems = useMemo(() => {
    // Eier wandern in die Brutkammer und tauchen im Inventar nicht mehr auf.
    const all = (data?.items ?? []).filter((it) => it.kind !== "egg");
    const q = search.trim().toLowerCase();
    const items = all.filter((it) => {
      // Filter
      if (kindFilter === "equipped" && !it.equipped) return false;
      if (kindFilter === "equipment" && !["weapon", "armor", "accessory"].includes(it.kind))
        return false;
      if (
        kindFilter !== "all" &&
        kindFilter !== "equipped" &&
        kindFilter !== "equipment" &&
        it.kind !== kindFilter
      )
        return false;
      // Suche
      if (q && !it.name.toLowerCase().includes(q) && !it.kind.toLowerCase().includes(q))
        return false;
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    items.sort((a, b) => {
      let cmp = 0;

      switch (sortKey) {
        case "power":
          cmp = strengthForSort(a) - strengthForSort(b);
          break;
        case "level":
          cmp = ((a as { level?: number }).level ?? 1) - ((b as { level?: number }).level ?? 1);
          break;
        case "rarity":
          cmp = (RARITY_RANK[a.rarity] ?? 0) - (RARITY_RANK[b.rarity] ?? 0);
          break;
        case "kind":
          cmp = a.kind.localeCompare(b.kind);
          break;
        case "value":
          cmp = sellPrice(a.rarity, a.qty) - sellPrice(b.rarity, b.qty);
          break;
        case "name":
          cmp = a.name.localeCompare(b.name);
          break;
      }
      if (cmp === 0) cmp = a.name.localeCompare(b.name);
      return cmp * dir;
    });
    return items;
  }, [data?.items, sortKey, sortDir, kindFilter, search]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["items"] });
    qc.invalidateQueries({ queryKey: ["equipment-layout"] });
    qc.invalidateQueries({ queryKey: ["combat-state"] });
    qc.invalidateQueries({ queryKey: ["my-monster"] });
    qc.invalidateQueries({ queryKey: ["my-monsters"] });
    // Anlegen und Ablegen aendern monsters.atk/def/hp_max. Fehlt das hier,
    // laeuft ein Kampf innerhalb der naechsten 60 Sekunden mit den alten
    // Werten und reisst mit "settle mismatch" (Sammy, 09.09.2026).
    qc.invalidateQueries({ queryKey: ["battle-v2", "monster"] });
  };

  // Effektiver Effekt eines Consumables (nur noch HP-Heilung nach Stufe 3/4).
  const effectiveEffect = (
    eff: Record<string, number>,
    m: typeof activeMonster,
  ): Record<string, number> => {
    if (!m) return { ...eff };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mAny = m as any;
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(eff)) {
      const want = Number(v) || 0;
      if (want <= 0) {
        out[k] = want;
        continue;
      }
      if (k === "hp") {
        out[k] = Math.max(0, Math.min(want, (mAny.hp_max ?? 0) - (mAny.hp ?? 0)));
      } else {
        out[k] = want;
      }
    }
    return out;
  };

  const monsterLevelForEquip = activeMonster?.level ?? layout?.level ?? 1;
  const equipLevelCap = maxEquipLevel(monsterLevelForEquip);
  const itemBlockedByLevel = (it: { level?: number | null; equipped?: boolean }): boolean => {
    if (it.equipped) return false;
    const lvl = it.level ?? null;
    return lvl != null && lvl > equipLevelCap;
  };

  const equipMut = useMutation({
    mutationFn: async (id: string) => {
      // Client-seitiger Vorab-Check: vermeidet einen Server-Throw, wenn
      // schon alle Accessoire-Slots belegt sind (sorgt sonst für eine
      // globale Runtime-Error-Meldung). UI-Hint statt Exception.
      const it = (data?.items ?? []).find((x) => x.id === id);
      if (it && !it.equipped) {
        const lvl = (it as { level?: number | null }).level ?? null;
        if (lvl != null && lvl > equipLevelCap) {
          throw new Error(
            t("inventory.errorLevelTooLow", {
              monsterLevel: monsterLevelForEquip,
              maxLevel: equipLevelCap,
            }),
          );
        }
        if (slotOfItem(it) === "accessory") {
          const unlocked = layout?.unlockedAccessorySlots ?? 1;
          const equippedAccs = (data?.items ?? []).filter(
            (x) => x.equipped && slotOfItem(x) === "accessory",
          );
          if (equippedAccs.length >= unlocked) {
            throw new Error(t("inventory.errorAccessoryFull", { count: unlocked }));
          }
        }
      }
      return equip({ data: { itemId: id } });
    },
    onSuccess: () => {
      invalidate();
      toast.success(t("inventory.toast.equipped"));
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const useMut = useMutation({
    mutationFn: (id: string) => consumeItemFn({ data: { itemId: id } }),
    onSuccess: () => {
      invalidate();
      toast.success(t("inventory.toast.used"));
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const sellMut = useMutation({
    mutationFn: (id: string) => sellFn({ data: { itemId: id } }),
    onSuccess: (r) => {
      invalidate();
      toast.success(t("inventory.toast.goldGained", { gold: r.gold }));
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const bulkMut = useMutation({
    mutationFn: (maxRarity: "common" | "rare" | "epic") => sellBulk({ data: { maxRarity } }),
    onSuccess: (r) => {
      invalidate();
      if (r.sold === 0) toast.info(t("inventory.toast.nothingToSell"));
      else toast.success(t("inventory.toast.bulkSold", { count: r.sold, gold: r.gold }));
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const equipBulkMut = useMutation({
    mutationFn: (args: { scope: "below_level" | "all_unequipped"; maxLevel?: number }) =>
      sellEquipBulkFn({ data: args }),
    onSuccess: (r) => {
      invalidate();
      if (r.sold === 0) toast.info(t("inventory.toast.noEquipToSell"));
      else toast.success(t("inventory.toast.equipBulkSold", { count: r.sold, gold: r.gold }));
      setConfirmUnequipped(false);
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const autoEquipMut = useMutation({
    mutationFn: () => autoEquipFn(),
    onSuccess: (r) => {
      invalidate();
      if (r.equipped === 0 && r.unequipped === 0) {
        toast.info(t("inventory.toast.alreadyOptimal"));
      } else {
        toast.success(
          t("inventory.toast.autoEquip", {
            equipped: r.equipped,
            unequipped: r.unequipped,
            slots: r.slotsChanged.length,
          }),
        );
      }
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const [maxLevel, setMaxLevel] = useState<number>(5);
  const [confirmUnequipped, setConfirmUnequipped] = useState(false);

  // Vorschau-Zahlen aus dem Items-Cache
  const equipItemsAll = (data?.items ?? []).filter(
    (it) => !it.equipped && ["weapon", "armor", "accessory"].includes(it.kind),
  );
  const equipUpToLevel = equipItemsAll.filter((it) => (it.level ?? 1) <= maxLevel).length;
  const equipUnequippedCount = equipItemsAll.length;

  // Eier werden jetzt in der Brutkammer ausgebrütet — keine Hatch-Mutation mehr hier.

  // ── Ausrüstungs-Raster ───────────────────────────────────────────────────
  const allItems = data?.items ?? [];
  const fixedSlots: readonly { slot: string; Icon: LucideIcon }[] = [
    { slot: "weapon", Icon: Sword },
    { slot: "helm", Icon: HardHat },
    { slot: "chest", Icon: Shirt },
    { slot: "pants", Icon: PersonStanding },
    { slot: "boots", Icon: Footprints },
    { slot: "gloves", Icon: Hand },
  ];
  const accSlots = layout?.accessorySlots ?? [{ index: 0, unlocked: true, hint: null }];
  const unlockedAcc = layout?.unlockedAccessorySlots ?? 1;
  const maxAcc = layout?.maxAccessorySlots ?? MAX_ACCESSORY_SLOTS;
  // Belegt-Zähler: feste Slots + ausgerüstete Accessoires (max = unlockedAcc)
  const equippedAccs = allItems.filter((i) => i.equipped && slotOfItem(i) === "accessory");
  const filledFixed = fixedSlots.filter((s) =>
    allItems.some((i) => i.equipped && slotOfItem(i) === s.slot),
  ).length;
  const filledCount = filledFixed + Math.min(equippedAccs.length, unlockedAcc);
  const totalSlots = fixedSlots.length + unlockedAcc;
  const unlockedAccSlots = accSlots.filter((s) => s.unlocked);
  const lockedAccSlots = accSlots.filter((s) => !s.unlocked);

  /**
   * Eine belegte Ausrüstungs-Kachel: Raritäts-Outline, Ink-Fläche, Werte,
   * Ablegen. Die Werte gehören hierher — ohne sie ist im Raster nicht
   * ablesbar, was ein angelegtes Teil bringt, und man müsste es unten in der
   * Liste suchen.
   */
  const renderFilledSlot = (
    key: string,
    label: string,
    Icon: LucideIcon,
    it: (typeof allItems)[number],
  ) => {
    const rar = asRarity(it.rarity);
    const slotStats = readStats(it.stats);
    return (
      <div
        key={key}
        className="flex min-h-[112px] flex-col gap-2 rounded-md border-2 bg-ink p-2.5"
        style={rarityBorder(rar)}
      >
        <MicroLabel className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
          <span className="min-w-0 truncate">{label}</span>
        </MicroLabel>
        <div className="flex items-center gap-2">
          <ItemIcon icon={<Icon className="h-4 w-4" strokeWidth={2.5} />} rarity={rar} size={32} />
          <div className="min-w-0">
            <div className="line-clamp-2 text-xs font-extrabold leading-tight [overflow-wrap:anywhere]">
              {it.name}
            </div>
            <div className="text-[10px] font-extrabold tabular-nums text-muted-foreground">
              {t("header.levelShort")} {(it as { level?: number }).level ?? 1}
            </div>
          </div>
        </div>
        {Object.keys(slotStats).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {Object.entries(slotStats).map(([k, v]) => (
              <StatChip key={k} statKey={k} value={v} compact surfaceClassName="bg-secondary" />
            ))}
          </div>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="mt-auto min-h-11 self-start px-2 sm:min-h-9"
          onClick={() => equipMut.mutate(it.id)}
          disabled={equipMut.isPending}
        >
          {t("inventory.unequip")}
        </Button>
      </div>
    );
  };

  /** Leere Kachel: gestrichelte Outline, kein Schatten — nichts zu greifen. */
  const renderEmptySlot = (key: string, label: string, Icon: LucideIcon) => (
    <div
      key={key}
      className="flex min-h-[112px] flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed border-border-soft p-2.5 text-center"
    >
      <Icon className="h-5 w-5 text-muted-foreground" strokeWidth={2.5} />
      <MicroLabel>{label}</MicroLabel>
      <div className="text-xs font-extrabold text-muted-foreground">{t("inventory.slotFree")}</div>
    </div>
  );

  // ── Item-Karte ───────────────────────────────────────────────────────────
  const renderItemCard = (it: (typeof sortedItems)[number]) => {
    const stats = readStats(it.stats);
    const isConsumable = it.kind === "consumable";
    const effective = isConsumable ? effectiveEffect(stats, activeMonster) : {};
    const effectiveTotal = isConsumable
      ? Object.values(effective).reduce((a, b) => a + (Number(b) || 0), 0)
      : 0;
    const onlyCureSick =
      isConsumable &&
      Object.keys(stats).length > 0 &&
      Object.keys(stats).every((k) => k === "cure_sick");
    const useDisabled = isConsumable && activeMonster != null && effectiveTotal === 0;
    const useDisabledReason = !useDisabled
      ? ""
      : onlyCureSick
        ? t("inventory.useDisabledNotSick")
        : t("inventory.useDisabledFull");
    const isEquip = isEquipKind(it.kind);
    const itSlot = slotOfItem(it);
    const isArmorSub = ["helm", "chest", "pants", "boots", "gloves"].includes(itSlot);
    const typeLabel = isArmorSub
      ? t(`inventory.slots.${itSlot}`, itSlot)
      : t(`inventory.kinds.${it.kind}`, it.kind);
    const tipText =
      (itSlot && t(`inventory.slotTips.${itSlot}`, "")) ||
      t(`inventory.kindTips.${it.kind}`, "") ||
      "Item";

    // Vergleich UND Markierung stammen aus derselben Funktion
    // (`evaluateUpgrade`) — dieselbe, nach der auch Auto-Equip und
    // Auto-Salvage serverseitig entscheiden. Vorher rechnete diese
    // Karte einen eigenen Score mit voll gewichtetem hp_max und
    // konnte damit ein anderes „schwächstes" Accessoire zeigen, als
    // der Server tatsächlich ersetzt hätte.
    const verdict =
      isEquip && !it.equipped ? evaluateUpgrade(it, allItems, monsterLevelForEquip) : null;
    let compareTarget: { name: string; stats: Record<string, number> } | null = null;
    let compareNote: string | null = null;
    if (verdict?.replaces) {
      compareTarget = {
        name: verdict.replaces.name,
        stats: readStats(verdict.replaces.stats),
      };
    } else if (verdict?.reason === "empty-slot") {
      compareNote =
        itSlot === "accessory" ? t("inventory.compare.accFree") : t("inventory.compare.slotEmpty");
    }

    const compareKeys = compareTarget
      ? Array.from(new Set([...Object.keys(stats), ...Object.keys(compareTarget.stats)]))
      : [];

    const rar = asRarity(it.rarity);
    const Icon = iconOfItem(it);
    const price = sellPrice(it.rarity, it.qty);
    const blocked =
      isEquip && itemBlockedByLevel(it as { level?: number | null; equipped?: boolean });

    // Kein Orange auf Item-Ebene: Der eine orange Knopf dieser Ansicht ist
    // „Beste anlegen" im Seitenkopf. Bei Dutzenden Items im Inventar wäre
    // sonst jede Kachel eine Primäraktion — und damit keine mehr.
    const equipButton = (
      <Button
        size="sm"
        variant="secondary"
        className="min-h-11 sm:min-h-9"
        onClick={() => equipMut.mutate(it.id)}
        disabled={equipMut.isPending || blocked}
      >
        {it.equipped ? t("inventory.unequip") : t("inventory.equip")}
      </Button>
    );

    return (
      <Panel
        key={it.id}
        holo={rar === "legendary"}
        className={it.equipped ? "opacity-70" : undefined}
        contentClassName="flex h-full flex-col p-3.5"
      >
        <div className="flex gap-2.5">
          <div className="relative shrink-0">
            <ItemIcon icon={<Icon className="h-5 w-5" strokeWidth={2.5} />} rarity={rar} />
            {it.qty > 1 && (
              <span
                className="pointer-events-none absolute -bottom-1.5 -right-1.5 rounded-full border-2 border-secondary bg-ink px-1.5 text-[10px] font-extrabold tabular-nums"
                aria-label={t("inventory.qty", { qty: it.qty })}
              >
                ×{it.qty}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <InfoTip text={tipText}>
              <span className="block">
                <span className="flex items-start justify-between gap-2">
                  <span className="line-clamp-2 min-w-0 text-sm font-extrabold leading-tight [overflow-wrap:anywhere]">
                    {it.name}
                  </span>
                  <RarityTag rarity={rar} label={rarityLabel(rar)} className="shrink-0" />
                </span>
                <span className="mt-1 block text-[11px] font-bold text-muted-foreground">
                  {isEquip
                    ? `${t("header.levelShort")} ${(it as { level?: number }).level ?? 1} · `
                    : ""}
                  {typeLabel} ⓘ
                </span>
              </span>
            </InfoTip>
            {(it.equipped || verdict) && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {it.equipped && (
                  <Pill style={{ background: "var(--primary)", color: "var(--ink)" }}>
                    {t("inventory.equippedBadge")}
                  </Pill>
                )}
                {verdict && (
                  <Pill
                    style={
                      verdict.isUpgrade
                        ? { background: "var(--hp-ally)", color: "var(--ink)" }
                        : { background: "var(--secondary)", color: "var(--muted-foreground)" }
                    }
                  >
                    {verdict.isUpgrade
                      ? t("inventory.compare.upgrade")
                      : t("inventory.compare.noUpgrade")}
                  </Pill>
                )}
              </div>
            )}
          </div>
        </div>

        {Object.keys(stats).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {Object.entries(stats).map(([k, v]) => {
              const effVal = isConsumable ? (effective[k] ?? 0) : Number(v);
              const isCapped =
                isConsumable && activeMonster != null && effVal === 0 && Number(v) > 0;
              const isPartial =
                isConsumable && activeMonster != null && effVal > 0 && effVal < Number(v);
              return (
                <StatChip key={k} statKey={k} value={v} struck={isCapped}>
                  {isPartial && (
                    <span className="text-[10px] font-bold text-gold">
                      {t("inventory.effectiveHint", { val: effVal })}
                    </span>
                  )}
                  {isCapped && (
                    <span className="text-[10px] font-bold text-muted-foreground">
                      ({k === "cure_sick" ? t("inventory.cappedNotSick") : t("inventory.cappedMax")}
                      )
                    </span>
                  )}
                </StatChip>
              );
            })}
          </div>
        )}

        {isEquip && !it.equipped && (compareTarget || compareNote) && (
          <div className="mt-3 rounded-md border-2 border-ink bg-ink/50 p-2">
            <div className="flex items-center justify-between gap-2">
              <MicroLabel>{t("inventory.compare.label")}</MicroLabel>
              {compareTarget && (
                <span className="line-clamp-1 min-w-0 text-[11px] font-bold text-muted-foreground [overflow-wrap:anywhere]">
                  vs. {compareTarget.name}
                </span>
              )}
            </div>
            {compareNote && (
              <div className="mt-1 text-[11px] font-bold text-muted-foreground">{compareNote}</div>
            )}
            {compareTarget && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {compareKeys.map((k) => {
                  const diff = (stats[k] ?? 0) - (compareTarget!.stats[k] ?? 0);
                  const sign = diff > 0 ? "+" : "";
                  const style: React.CSSProperties =
                    diff > 0
                      ? { background: "var(--hp-ally)", color: "var(--ink)" }
                      : diff < 0
                        ? { background: "var(--destructive)", color: "var(--foreground)" }
                        : { background: "var(--secondary)", color: "var(--muted-foreground)" };
                  return (
                    <span
                      key={k}
                      style={style}
                      className="rounded-md border-2 border-ink px-1.5 py-0.5 font-display text-[10px] uppercase tracking-[0.1em] tabular-nums"
                    >
                      {sign}
                      {diff} {t(`inventory.stats.${k}`, k)}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="mt-auto flex flex-wrap items-center gap-2 pt-3.5">
          {isEquip &&
            (blocked ? (
              <InfoTip
                text={t("inventory.equipLevelTip", {
                  required: requiredLevelForItem((it as { level?: number }).level ?? 1),
                  current: monsterLevelForEquip,
                  cap: equipLevelCap,
                })}
              >
                {equipButton}
              </InfoTip>
            ) : (
              equipButton
            ))}
          {isConsumable && (
            <InfoTip text={useDisabled ? useDisabledReason : t("inventory.applyTip")}>
              <Button
                size="sm"
                variant="secondary"
                className="min-h-11 sm:min-h-9"
                onClick={() => useMut.mutate(it.id)}
                disabled={useMut.isPending || useDisabled}
              >
                <Sparkles strokeWidth={2.5} /> {t("inventory.use")}
              </Button>
            </InfoTip>
          )}
          {!it.equipped && (
            <>
              <Button
                size="sm"
                variant="ghost"
                className="min-h-11 sm:min-h-9"
                onClick={() => sellMut.mutate(it.id)}
                disabled={sellMut.isPending}
              >
                <Coins strokeWidth={2.5} /> {t("inventory.sell")}
              </Button>
              <GoldAmount value={price} className="ml-auto" />
            </>
          )}
        </div>
      </Panel>
    );
  };

  const equipmentItems = sortedItems.filter((it) =>
    ["weapon", "armor", "accessory"].includes(it.kind),
  );
  const consumableItems = sortedItems.filter((it) => it.kind === "consumable");
  const knownKinds = new Set(["weapon", "armor", "accessory", "consumable"]);
  const otherItems = sortedItems.filter((it) => !knownKinds.has(it.kind));

  const renderSection = (key: string, titleKey: string, items: typeof sortedItems) => {
    if (items.length === 0) return null;
    return (
      <details key={key} open className="group mt-5">
        <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-2 rounded-lg border-2 border-ink bg-secondary px-3.5 py-2 shadow-hard-sm">
          <h3 className="text-lg leading-none">
            {t(titleKey)}{" "}
            <span className="font-sans text-xs font-extrabold tabular-nums text-muted-foreground [text-shadow:none]">
              ({items.length})
            </span>
          </h3>
          <span className="font-display text-[10px] uppercase tracking-[0.1em] text-muted-foreground group-open:hidden">
            {t("inventory.showSlots")}
          </span>
          <span className="hidden font-display text-[10px] uppercase tracking-[0.1em] text-muted-foreground group-open:inline">
            {t("inventory.hideSlots")}
          </span>
        </summary>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map(renderItemCard)}
        </div>
      </details>
    );
  };

  const kindFilterItems = KIND_FILTER_VALUES.map((v) => [v, t(`inventory.filter.${v}`)] as const);

  return (
    <div className="relative min-h-screen">
      <div aria-hidden className="halftone pointer-events-none absolute inset-0 opacity-50" />
      <ScreenContainer className="relative z-[1]">
        <PageHeader
          title={t("inventory.title")}
          icon={<Backpack className="h-[22px] w-[22px] text-primary" strokeWidth={2.5} />}
          actions={
            <>
              {/* Der EINE orange Knopf dieser Ansicht — die Primäraktion des
                  Inventars. Alles andere (Ausrüsten, Benutzen, Verkaufen) ist
                  bewusst secondary/ghost, sonst wären Dutzende Kacheln orange. */}
              <InfoTip text={t("inventory.autoEquipTip")}>
                <Button
                  size="sm"
                  className="min-h-11 sm:min-h-9"
                  disabled={autoEquipMut.isPending}
                  onClick={() => autoEquipMut.mutate()}
                >
                  <Wand2 strokeWidth={2.5} /> {t("inventory.autoEquipBtn")}
                </Button>
              </InfoTip>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="min-h-11 sm:min-h-9"
                    disabled={bulkMut.isPending}
                    title={t("inventory.bulkSellTip")}
                    data-tutorial-target="bulk-sell"
                  >
                    <PackageMinus strokeWidth={2.5} /> {t("inventory.bulkSellBtn")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("inventory.bulkDialog.title")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("inventory.bulkDialog.description")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>

                  <div className="space-y-2">
                    <MicroLabel>{t("inventory.bulkDialog.byRarityLabel")}</MicroLabel>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Button
                        variant="outline"
                        className="flex-1"
                        disabled={bulkMut.isPending}
                        onClick={() => bulkMut.mutate("common")}
                      >
                        {t("inventory.bulkDialog.onlyCommon")}
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1"
                        disabled={bulkMut.isPending}
                        onClick={() => bulkMut.mutate("rare")}
                      >
                        {t("inventory.bulkDialog.commonRare")}
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1"
                        disabled={bulkMut.isPending}
                        onClick={() => bulkMut.mutate("epic")}
                      >
                        {t("inventory.bulkDialog.upToEpic")}
                      </Button>
                    </div>
                  </div>

                  <div className="mt-2 space-y-2 border-t-2 border-ink pt-3">
                    <MicroLabel>{t("inventory.bulkDialog.byEquipLabel")}</MicroLabel>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <label className="flex items-center gap-2 text-sm font-bold">
                        <span className="text-muted-foreground">
                          {t("inventory.bulkDialog.maxLevel")}
                        </span>
                        <Input
                          type="number"
                          min={1}
                          max={99}
                          value={maxLevel}
                          onChange={(e) =>
                            setMaxLevel(Math.max(1, Math.min(99, Number(e.target.value) || 1)))
                          }
                          className="w-20 text-center tabular-nums"
                        />
                      </label>
                      <Button
                        variant="outline"
                        className="flex-1"
                        disabled={equipBulkMut.isPending || equipUpToLevel === 0}
                        onClick={() => equipBulkMut.mutate({ scope: "below_level", maxLevel })}
                      >
                        {t("inventory.bulkDialog.sellUpToLevel", {
                          level: maxLevel,
                          count: equipUpToLevel,
                        })}
                      </Button>
                    </div>
                    <Button
                      variant={confirmUnequipped ? "destructive" : "outline"}
                      className="w-full"
                      disabled={equipBulkMut.isPending || equipUnequippedCount === 0}
                      onClick={() => {
                        if (!confirmUnequipped) {
                          setConfirmUnequipped(true);
                          window.setTimeout(() => setConfirmUnequipped(false), 3000);
                          return;
                        }
                        equipBulkMut.mutate({ scope: "all_unequipped" });
                      }}
                    >
                      {confirmUnequipped
                        ? t("inventory.bulkDialog.confirmUnequipped", {
                            count: equipUnequippedCount,
                          })
                        : t("inventory.bulkDialog.sellAllUnequipped", {
                            count: equipUnequippedCount,
                          })}
                    </Button>
                  </div>

                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("inventory.bulkDialog.close")}</AlertDialogCancel>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          }
        />

        {/* ── Ausrüstung ─────────────────────────────────────────────────── */}
        <Panel grid contentClassName="p-3.5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-lg leading-none">{t("inventory.equipment")}</h3>
            <span className="text-xs font-extrabold tabular-nums text-muted-foreground">
              {t("inventory.slotsFilled", { filled: filledCount, total: totalSlots })}
            </span>
          </div>

          <div className="mt-3 grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(150px,1fr))]">
            {fixedSlots.map(({ slot, Icon }) => {
              const label = t(`inventory.slots.${slot}`, slot);
              const it = allItems.find((i) => i.equipped && slotOfItem(i) === slot);
              return it
                ? renderFilledSlot(slot, label, Icon, it)
                : renderEmptySlot(slot, label, Icon);
            })}
            {unlockedAccSlots.map((slotInfo) => {
              const label = `${t("inventory.slots.accessory")} #${slotInfo.index + 1}`;
              const it = equippedAccs[slotInfo.index];
              return it
                ? renderFilledSlot(`acc-${slotInfo.index}`, label, Gem, it)
                : renderEmptySlot(`acc-${slotInfo.index}`, label, Gem);
            })}
          </div>

          <MicroLabel className="mt-2.5 flex items-center gap-1.5">
            <Gem className="h-3.5 w-3.5" strokeWidth={2.5} />
            <span>
              {t("inventory.slots.accessory")} ·{" "}
              <span className="tabular-nums">
                {unlockedAcc}/{maxAcc}
              </span>
            </span>
          </MicroLabel>

          {lockedAccSlots.length > 0 && (
            <details className="group mt-2.5">
              <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-2 rounded-md border-2 border-dashed border-border-soft px-3 py-2 text-xs font-extrabold text-muted-foreground">
                <span className="flex items-center gap-2">
                  <Lock className="h-3.5 w-3.5" strokeWidth={2.5} />
                  {t("inventory.lockedSlots", { count: lockedAccSlots.length })}
                </span>
                <span className="font-display text-[10px] uppercase tracking-[0.1em] group-open:hidden">
                  {t("inventory.showSlots")}
                </span>
                <span className="hidden font-display text-[10px] uppercase tracking-[0.1em] group-open:inline">
                  {t("inventory.hideSlots")}
                </span>
              </summary>
              <div className="mt-2.5 grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(150px,1fr))]">
                {lockedAccSlots.map((slotInfo) => (
                  <div
                    key={slotInfo.index}
                    className="flex min-h-[112px] flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed border-border-soft bg-ink/30 p-2.5 text-center"
                  >
                    <Lock className="h-5 w-5 text-muted-foreground" strokeWidth={2.5} />
                    <MicroLabel>
                      {t("inventory.slots.accessory")} #{slotInfo.index + 1}
                    </MicroLabel>
                    <div className="text-[11px] font-bold text-muted-foreground">
                      {slotInfo.hint ?? t("inventory.slotLocked")}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </Panel>

        {/* ── Auto-Salvage ───────────────────────────────────────────────── */}
        <AutoSalvageControl />

        {/* ── Sortieren / Filtern / Suchen ───────────────────────────────── */}
        {!isLoading && (data?.items.length ?? 0) > 0 && (
          <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2.5">
            <div className="flex items-center gap-2">
              <MicroLabel>{t("inventory.sort.label")}</MicroLabel>
              <Select value={sortKey} onValueChange={(v) => setSortKey(v as SortKey)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="power">{t("inventory.sort.power")}</SelectItem>
                  <SelectItem value="level">{t("inventory.sort.level")}</SelectItem>
                  <SelectItem value="rarity">{t("inventory.sort.rarity")}</SelectItem>
                  <SelectItem value="kind">{t("inventory.sort.kind")}</SelectItem>
                  <SelectItem value="value">{t("inventory.sort.value")}</SelectItem>
                  <SelectItem value="name">{t("inventory.sort.name")}</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                className="min-h-11 sm:min-h-9"
                onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              >
                <ArrowUpDown strokeWidth={2.5} />
                {sortDir === "asc" ? t("inventory.sort.asc") : t("inventory.sort.desc")}
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <MicroLabel>{t("inventory.filter.label")}</MicroLabel>
              <FilterPills
                items={kindFilterItems}
                value={kindFilter}
                onChange={(v) => setKindFilter(v)}
              />
            </div>

            <div className="relative ml-auto w-full sm:w-[220px]">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                strokeWidth={2.5}
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("inventory.filter.searchPlaceholder")}
                className="pl-8 pr-11"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-1 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
                  aria-label={t("inventory.filter.clearSearch")}
                >
                  <X className="h-4 w-4" strokeWidth={2.5} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Zustände & Listen ──────────────────────────────────────────── */}
        {isLoading ? (
          <Panel className="mt-5" contentClassName="p-6 text-center">
            <p className="text-sm font-bold text-muted-foreground">{t("inventory.loading")}</p>
          </Panel>
        ) : data?.items.length === 0 ? (
          <Panel
            className="mt-5"
            contentClassName="flex flex-col items-center gap-2 p-6 text-center"
          >
            <Package className="h-6 w-6 text-muted-foreground" strokeWidth={2.5} />
            <p className="text-sm font-bold text-muted-foreground">{t("inventory.empty")}</p>
          </Panel>
        ) : sortedItems.length === 0 ? (
          (data?.items.length ?? 0) > 0 ? (
            <Panel className="mt-5" contentClassName="p-6 text-center">
              <p className="text-sm font-bold text-muted-foreground">{t("inventory.noMatch")}</p>
            </Panel>
          ) : null
        ) : (
          <>
            {renderSection("equipment", "inventory.sections.equipment", equipmentItems)}
            {renderSection("consumables", "inventory.sections.consumables", consumableItems)}
            {renderSection("special", "inventory.sections.special", otherItems)}
          </>
        )}
      </ScreenContainer>
    </div>
  );
}

// ── Auto-Salvage-Setting ───────────────────────────────────────────────
// Schwelle (Item-Level) auf dem Profil. Drops mit Level < Schwelle werden
// im Kampf automatisch zu Gold umgewandelt. 0 = aus.
function AutoSalvageControl() {
  const { t } = useTranslation("game");
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["profile-auto-salvage"],
    queryFn: async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return { auto_salvage_level: 0 };
      const { data: row } = await (
        supabase.from("profiles") as unknown as {
          select: (s: string) => {
            eq: (
              k: string,
              v: string,
            ) => { maybeSingle: () => Promise<{ data: { auto_salvage_level: number } | null }> };
          };
        }
      )
        .select("auto_salvage_level")
        .eq("id", u.user.id)
        .maybeSingle();
      return row ?? { auto_salvage_level: 0 };
    },
    staleTime: 30_000,
  });
  const mut = useMutation({
    mutationFn: async (level: number) => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) throw new Error(t("inventory.autoSalvage.notLoggedIn"));
      const { error } = await (
        supabase.from("profiles") as unknown as {
          update: (v: Record<string, number>) => {
            eq: (k: string, v: string) => Promise<{ error: { message: string } | null }>;
          };
        }
      )
        .update({ auto_salvage_level: level })
        .eq("id", u.user.id);
      if (error) throw new Error(error.message);
      return level;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile-auto-salvage"] });
      toast.success(t("inventory.autoSalvage.updated"));
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });
  const current = data?.auto_salvage_level ?? 0;
  // Der Schalter wirkt seit dem 02.09.2026 wirklich (Port in den Settle-Layer,
  // vorher las ihn nur der tote Idle-Kampf). Genau deshalb muss man ihm jetzt
  // ansehen, ob er an oder aus ist — eine „0" im Zahlenfeld ist als Aus-Zustand
  // zu leise. Deshalb steht er als eigenes Panel auf dem Schirm und nicht als
  // Kleinteil in der Kopfzeile: Der Kampf-Screen verweist auf ihn.
  const off = current <= 0;
  return (
    <Panel tone={off ? "default" : "primary"} className="mt-3.5" contentClassName="p-3.5">
      <div className="flex flex-wrap items-center gap-3">
        <div
          className="grid h-11 w-11 shrink-0 place-items-center rounded-md border-2 border-ink"
          style={{ background: off ? "var(--secondary)" : "var(--accent)" }}
        >
          <PackageMinus className="h-5 w-5" strokeWidth={2.5} />
        </div>
        <MicroLabel className="min-w-0 flex-1">
          <InfoTip text={t("inventory.autoSalvage.tip")}>
            <span>{t("inventory.autoSalvage.label")} ⓘ</span>
          </InfoTip>
        </MicroLabel>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={0}
            max={50}
            value={current}
            disabled={isLoading || mut.isPending}
            onChange={(e) => {
              const v = Math.max(0, Math.min(50, Number(e.target.value) || 0));
              mut.mutate(v);
            }}
            className="w-20 text-center tabular-nums"
          />
          <Pill
            style={
              off
                ? { background: "var(--secondary)", color: "var(--muted-foreground)" }
                : { background: "var(--hp-ally)", color: "var(--ink)" }
            }
          >
            {off ? t("inventory.autoSalvage.off") : t("inventory.autoSalvage.on")}
          </Pill>
        </div>
      </div>
    </Panel>
  );
}
